// Estimated impact of Sri Taru trees: CO2 absorbed and farmers' fruit income.
//
// Calibrated on the SriTaru Sankalpam 2024 project figures (SankalpTaru
// completion report FY2024 + our 2024-25 project story), for 5,000 trees
// (4,135 mango, 865 guava):
//   - 45.68 t CO2 absorbed in the first year (2024-25)      -> ~9.1 kg per tree
//   - 540+ t CO2 cumulative over the first five years       -> ~108 kg per tree
//   - Rs 12.4M a year from mango and Rs 1.9M from guava at full bearing
//                                                           -> ~Rs 3,000 / mango tree, ~Rs 2,200 / guava tree
// CO2 from biomass follows the IPCC method (2006 Guidelines, Vol. 4: carbon
// is ~47% of dry biomass; CO2 = C x 44/12). These are estimates: they assume
// the trees survive and are cared for, and real yields depend on rain,
// variety and market prices.

// kg CO2 absorbed per tree in each year after planting (year 1, 2, ...).
// Years 1-5 sum to the project's 108 kg; afterwards growth slows to a ceiling.
const CO2_KG_BY_YEAR = [9.1, 14.5, 21, 27.5, 35.9, 42, 48, 54, 60];
const CO2_KG_MAX = 60;

export const SPECIES = {
  mango: {
    label: "Mango",
    incomeAtMaturity: 3000, // Rs per tree per year at full bearing
    // share of full yield in year 1, 2, ... (grafted mango starts bearing in year 3-4)
    yieldRamp: [0, 0, 0.1, 0.25, 0.4, 0.6, 0.8, 1],
  },
  guava: {
    label: "Guava",
    incomeAtMaturity: 2200,
    yieldRamp: [0, 0.3, 0.6, 1],
  },
};

// What each campaign planted and when. 2024 is from SankalpTaru's report.
// 2025's species split and planting month are not confirmed yet (assumed
// mango, planted Aug 2025); 2026 is not planted yet.
export const CAMPAIGNS = {
  2024: { planted: "2024-08-01", species: { mango: 4135, guava: 865 }, confirmed: true },
  2025: { planted: "2025-08-01", species: null, assumeSpecies: "mango", confirmed: false },
  2026: { planted: null },
};

const co2ForYear = (y) => (y < CO2_KG_BY_YEAR.length ? CO2_KG_BY_YEAR[y] : CO2_KG_MAX);

// kg CO2 absorbed by one tree from planting until `ageYears` (fractional).
export function co2PerTreeToDate(ageYears) {
  let total = 0;
  for (let y = 0; y < Math.floor(ageYears); y++) total += co2ForYear(y);
  return total + (ageYears % 1) * co2ForYear(Math.floor(ageYears));
}

const ramp = (sp, ageYears) => {
  const r = SPECIES[sp].yieldRamp;
  const y = Math.floor(ageYears);
  return y < r.length ? r[y] : 1;
};

// Rs earned by one tree of species `sp` from planting until `ageYears` (fractional).
function incomePerTreeToDate(sp, ageYears) {
  const full = SPECIES[sp].incomeAtMaturity;
  let total = 0;
  for (let y = 0; y < Math.floor(ageYears); y++) total += full * ramp(sp, y);
  return total + (ageYears % 1) * full * ramp(sp, ageYears);
}

const yearsSince = (iso, now) => Math.max(0, (now - new Date(iso)) / (365.25 * 24 * 3600 * 1000));

// treesByYear: { 2024: 5000, 2025: 10119, 2026: 896 } (planted trees per campaign).
// speciesMix (optional) replaces the campaign mix, e.g. { mango: 1 } for one farm.
export function estimateImpact(treesByYear, now = new Date(), speciesMix = null) {
  let co2ToDateKg = 0, co2ThisYearKg = 0, co2FiveYearKg = 0;
  let incomeThisYear = 0, incomeToDate = 0, incomeAtMaturity = 0, plantedTrees = 0, pendingTrees = 0;
  const bySpecies = {};
  for (const [year, trees] of Object.entries(treesByYear)) {
    const c = CAMPAIGNS[year];
    if (!c || !c.planted) { pendingTrees += trees; continue; }
    const age = yearsSince(c.planted, now);
    if (age <= 0) { pendingTrees += trees; continue; }
    plantedTrees += trees;
    // Split this campaign's trees by species (scaled if the DB count differs from the report).
    const mix = speciesMix || c.species || { [c.assumeSpecies]: trees };
    const mixTotal = Object.values(mix).reduce((a, b) => a + b, 0);
    for (const [sp, n] of Object.entries(mix)) {
      const t = (n / mixTotal) * trees;
      bySpecies[sp] = (bySpecies[sp] || 0) + t;
      incomeThisYear += t * SPECIES[sp].incomeAtMaturity * ramp(sp, age);
      incomeToDate += t * incomePerTreeToDate(sp, age);
      incomeAtMaturity += t * SPECIES[sp].incomeAtMaturity;
    }
    co2ToDateKg += trees * co2PerTreeToDate(age);
    co2ThisYearKg += trees * co2ForYear(Math.floor(age));
    co2FiveYearKg += trees * co2PerTreeToDate(5);
  }
  return {
    plantedTrees, pendingTrees, bySpecies,
    co2ToDateTonnes: co2ToDateKg / 1000,
    co2PerYearNowTonnes: co2ThisYearKg / 1000,
    co2FiveYearTonnes: co2FiveYearKg / 1000,
    incomeThisYearInr: incomeThisYear,       // current yearly rate
    incomeToDateInr: incomeToDate,           // earned since planting
    incomeAtMaturityInr: incomeAtMaturity,
  };
}

export const IMPACT_SOURCES = [
  "SankalpTaru Foundation, Project Completion Report FY2024 (species and farms)",
  "SriTaru Sankalpam 2024-25 project story (CO2 and income projections for the 2024 planting)",
  "IPCC 2006 Guidelines for National Greenhouse Gas Inventories, Vol. 4 (biomass carbon method)",
];

export const formatInr = (n) => {
  if (n >= 1e7) return `₹${(n / 1e7).toFixed(2)} crore`;
  if (n >= 1e5) return `₹${(n / 1e5).toFixed(1)} lakh`;
  return `₹${Math.round(n).toLocaleString("en-IN")}`;
};
